import { TrainingZoneLegend } from '../TrainingZoneLegend';
import { trainingZoneNumber } from '../../utils/trainingZones';
/**
 * HR Zones Chart
 * Vertical gradient bars, one per zone (Z1–Z5), with value labels above
 * and zone labels below. Used as a standalone summary or a fallback when
 * per-week zone data is unavailable.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { svgEl, gridLine, axisLabel } from './svgUtils';
import type { HrZone } from './types';

const VW = 260;
const VH = 170;
const MT = 10;
const MB = 22;
const Y_AXIS_W = 22;
const LABEL_MUTED = 'var(--chart-label, #9AA1B1)';

interface HrLayout {
  mx: number;
  mr: number;
  cw: number;
  ch: number;
  maxH: number;
  nb: number;
  bw: number;
  gapX: number;
}

function computeLayout(zones: HrZone[], mx: number, mr: number): HrLayout | null {
  if (!zones.length) return null;
  const cw = VW - mx - mr;
  const ch = VH - MT - MB;
  const maxH = Math.max(...zones.map((z) => z.hrs)) * 1.18;
  const nb = zones.length;
  const bw = Math.floor((cw / nb) * 0.6);
  const gapX = cw / nb - bw;
  return { mx, mr, cw, ch, maxH, nb, bw, gapX };
}

function render(svg: SVGSVGElement, zones: HrZone[], layout: HrLayout, htmlYLabels: boolean): void {
  svg.innerHTML = '';
  const { mx: MX, mr: MR, cw, ch, maxH, nb, bw, gapX } = layout;

  const defs = svgEl('defs');
  zones.forEach((z, i) => {
    const g = svgEl('linearGradient', { class: 'chart-bar-gradient', id: `hrz${i}`, x1: 0, y1: 0, x2: 0, y2: 1 });
    const s1 = svgEl('stop', { offset: '0%' }); s1.setAttribute('stop-color', z.color);
    const s2 = svgEl('stop', { offset: '100%' }); s2.setAttribute('stop-color', z.color); s2.setAttribute('stop-opacity', '0.45');
    g.appendChild(s1); g.appendChild(s2); defs.appendChild(g);
  });
  svg.appendChild(defs);

  for (let i = 0; i <= 3; i++) {
    const y = MT + (ch / 3) * i;
    gridLine(svg, MX, y, VW - MR, y);
    if (!htmlYLabels) {
      axisLabel(svg, MX - 4, y + 3.5, `${Math.round(maxH * (1 - i / 3))}h`);
    }
  }

  zones.forEach((z, i) => {
    const bx = MX + (cw / nb) * i + gapX / 2;
    const bh = (z.hrs / maxH) * ch;
    const by = MT + ch - bh;
    svg.appendChild(svgEl('rect', { x: bx, y: by, width: bw, height: Math.max(bh, 1), rx: 5, fill: `url(#hrz${i})` }));

    const vt = svgEl('text', {
      x: bx + bw / 2, y: by - 4, 'text-anchor': 'middle', 'font-size': 8.5, 'font-weight': 800,
      fill: z.color, 'font-family': 'Plus Jakarta Sans,Inter,sans-serif',
    });
    vt.textContent = z.hrs.toFixed(1);
    svg.appendChild(vt);

    axisLabel(svg, bx + bw / 2, VH - 6, z.lbl, 'middle');
  });
}

export interface ChartHrZonesProps {
  zones: HrZone[];
  flush?: boolean;
  style?: React.CSSProperties;
}

export function ChartHrZones({ zones, flush = false, style }: ChartHrZonesProps) {
  const ref = useRef<SVGSVGElement>(null);
  const mx = flush ? 0 : 34;
  const mr = flush ? 0 : 8;
  const htmlYLabels = flush;
  const layout = useMemo(() => computeLayout(zones, mx, mr), [zones, mx, mr]);

  const yTicks = useMemo(() => {
    if (!layout || !htmlYLabels) return [];
    return [0, 1, 2, 3].map((i) => ({
      value: `${Math.round(layout.maxH * (1 - i / 3))}h`,
      topPct: ((MT + (layout.ch / 3) * i) / VH) * 100,
    }));
  }, [layout, htmlYLabels]);

  useEffect(() => {
    if (ref.current && layout) render(ref.current, zones, layout, htmlYLabels);
  }, [zones, layout, htmlYLabels]);

  return (
    <div style={style}>
      <TrainingZoneLegend compact zones={zones.map((zone, index) => ({ number: trainingZoneNumber(zone.lbl) ?? index + 1 }))} />
    <div style={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0, marginTop: 12 }}>
      {htmlYLabels && (
        <div style={{ position: 'relative', width: Y_AXIS_W, flexShrink: 0, height: VH, pointerEvents: 'none' }}>
          {yTicks.map((tick) => (
            <span
              key={tick.value}
              style={{
                position: 'absolute',
                right: 2,
                top: `${tick.topPct}%`,
                transform: 'translateY(-50%)',
                fontSize: 9,
                fontWeight: 600,
                color: LABEL_MUTED,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {tick.value}
            </span>
          ))}
        </div>
      )}
      <svg
        ref={ref}
        viewBox={`0 0 ${VW} ${VH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: VH, display: 'block', flex: 1, minWidth: 0, flexShrink: 0 }}
      />
    </div>
    </div>
  );
}
